import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BookUser, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/common/Loading";

const LABELS = {
  display_name: "Name", headline: "Headline", handle: "Handle", email: "Email",
  phone: "Phone", org: "Organization", title: "Title", city: "City", website: "Website",
  linkedin: "LinkedIn",
};

/** Recipient view of a shared contact card. Only the sender's selected fields exist here. */
export default function SharedCard() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(undefined);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    api.get(`/crm/card/shared/${token}`)
      .then((r) => setCard(r.data))
      .catch(() => setCard(null));
  }, [token]);

  const save = async () => {
    try {
      const { data } = await api.post(`/crm/card/shared/${token}/save`);
      setSaved(data.contact_id);
      toast.success(data.already_saved ? "Already in your contacts" : "Saved to your contacts");
    } catch { toast.error("Couldn't save this card"); }
  };

  if (card === undefined) return <Loading />;
  if (card === null) return (
    <div className="py-20 text-center" data-testid="shared-card-unavailable">
      <h1 className="text-2xl font-[680]">This card isn't available</h1>
      <p className="mt-2 text-muted-foreground">
        The link may have expired or been revoked by its owner.
      </p>
      <Button className="mt-6" onClick={() => navigate("/dashboard/contacts")}>
        Go to contacts
      </Button>
    </div>
  );

  return (
    <div className="mx-auto max-w-md" data-testid="shared-card-page">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-3">
          {card.profile?.headshot_url
            ? <img src={card.profile.headshot_url} alt="" className="h-14 w-14 rounded-full object-cover" />
            : <span className="h-14 w-14 rounded-full bg-secondary" />}
          <div className="min-w-0">
            <h1 className="truncate text-xl font-[680]" data-testid="shared-card-name">
              {card.fields.display_name || card.profile?.display_name || "Startup Otter member"}
            </h1>
            {card.fields.headline && (
              <p className="truncate text-sm text-muted-foreground">{card.fields.headline}</p>
            )}
          </div>
        </div>

        <dl className="mt-5 space-y-2.5" data-testid="shared-card-fields">
          {Object.entries(card.fields)
            .filter(([k]) => !["display_name", "headline"].includes(k))
            .map(([k, v]) => (
              <div key={k} className="flex gap-3 text-sm">
                <dt className="w-28 shrink-0 text-muted-foreground">{LABELS[k] || k}</dt>
                <dd className="min-w-0 flex-1 break-words" data-testid={`shared-field-${k}`}>{v}</dd>
              </div>
            ))}
        </dl>

        {!card.is_own_card && (
          <Button className="mt-6 w-full" onClick={save} disabled={!!saved}
            data-testid="shared-card-save">
            {saved ? <><Check className="mr-1.5 h-4 w-4" /> Saved</>
              : <><BookUser className="mr-1.5 h-4 w-4" /> Save to my contacts</>}
          </Button>
        )}
        {saved && (
          <Button variant="outline" className="mt-2 w-full" data-testid="shared-card-open"
            onClick={() => navigate(`/dashboard/contacts/${saved}`)}>
            Open contact
          </Button>
        )}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Saving this card adds it to your own private contacts. It gives you no access to
          their contacts.
        </p>
      </div>
    </div>
  );
}
